"use client"

/**
 * PauseOverlayAd — small corner-docked ad card shown when a user pauses a
 * video they've already been watching (never on the very first frame — see
 * the `hasPlayedOnce` gate in booru-detail-modal.tsx). Modeled on the
 * "paused video → small ad card in the corner" pattern common on cam/tube
 * sites: doesn't block or cover the paused frame, disappears the moment
 * playback resumes, and is dismissible on its own.
 *
 * Uses ExoClick's actual "In-Video Banner (VAST)" zone type set to the
 * "On Pause" trigger — a real NonLinear (static image) VAST creative, not
 * a video. Previously reused the "grid" placement's display zones (a
 * generic rectangle iframe with no real "pause" semantics on ExoClick's
 * side) since no dedicated zone existed yet; that zone now does.
 */

import { useEffect, useRef, useState } from "react"
import { Badge } from "@/components/ui/badge"
import { X } from "@/lib/icons"
import { useAdStore } from "@/lib/ad-store"
import { useHasConsent } from "@/components/cookie-consent"
import { useAdTier } from "@/lib/ad-tier"
import { cn } from "@/lib/utils"
import { parseVAST, fireTrackingUrls, type VASTResult } from "@/lib/vast-parser"
import { trackZoneImpression, trackZoneClick } from "@/lib/ad-zone-tracking"
import { isAdTestModeOn } from "@/lib/ad-cooldown"
import { isGlobalAdsOff } from "@/lib/global-ads-off"
import { useTranslations } from "next-intl"

interface ZoneData {
  zone_id: string
  vast_url: string | null
}

interface PauseOverlayAdProps {
  /** Auto-dismiss after this many seconds. Not a skip gate (nothing here
   * blocks playback) — just housekeeping so a forgotten paused tab doesn't
   * hold a stale ad on screen indefinitely. */
  autoDismissSecs?: number
  onDismiss: () => void
  className?: string
}

export function PauseOverlayAd({ autoDismissSecs = 20, onDismiss, className }: PauseOverlayAdProps) {
  const t = useTranslations("pauseOverlayAd")
  const hasConsent = useHasConsent()
  const { isPremiumUser } = useAdStore()
  // Force Ads test mode escape hatch — see the matching comment on
  // NetworkDisplayAd.tsx. Admin/super_admin accounts always read
  // isPremiumUser: true, so without this, Force Ads could never actually
  // force this to show for its only realistic tester.
  const effectivePremiumUser = (isPremiumUser || isGlobalAdsOff()) && !isAdTestModeOn()
  const adTier = useAdTier()
  const [nonLinear, setNonLinear] = useState<VASTResult["nonLinearAds"][number] | null>(null)
  const [zoneId, setZoneId] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [remaining, setRemaining] = useState(autoDismissSecs)
  const impressionTracked = useRef(false)
  const trackingRef = useRef<VASTResult["tracking"] | null>(null)

  useEffect(() => {
    if (!hasConsent || effectivePremiumUser) return
    let cancelled = false

    fetch(`/api/ads/network-grid-ad?type=in_video_pause_banner&tier=${adTier}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(async (d: ZoneData | null) => {
        if (cancelled || !d?.vast_url) return
        const result = await parseVAST(d.vast_url)
        if (cancelled || !result?.nonLinearAds.length) return
        setNonLinear(result.nonLinearAds[0])
        trackingRef.current = result.tracking
        setZoneId(d.zone_id)
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true) })

    return () => { cancelled = true }
  }, [hasConsent, effectivePremiumUser, adTier])

  // Impression — both the network's own VAST pixel and our zone-level record.
  useEffect(() => {
    if (!nonLinear || impressionTracked.current) return
    impressionTracked.current = true
    if (trackingRef.current) fireTrackingUrls(trackingRef.current.impression)
    trackZoneImpression({ zoneId })
  }, [nonLinear, zoneId])

  // Countdown
  useEffect(() => {
    if (!nonLinear) return
    const interval = setInterval(() => {
      setRemaining((r) => Math.max(0, r - 1))
    }, 1000)
    return () => clearInterval(interval)
  }, [nonLinear])

  // Auto-dismiss, as its own effect rather than inline in the countdown's
  // setRemaining updater above. Calling onDismiss() (the parent's setState)
  // from inside another component's state updater function is exactly the
  // "Cannot update a component while rendering a different component" React
  // error — reported live via BooruDetailModal's console. Updater functions
  // can run during React's render/commit work for THIS component; a parent
  // setState triggered from inside one lands mid-render for the parent.
  // Reacting to the resulting `remaining === 0` in a separate effect defers
  // the call to its own commit phase instead.
  useEffect(() => {
    if (nonLinear && remaining === 0) onDismiss()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonLinear, remaining])

  if (!hasConsent || effectivePremiumUser || !loaded || !nonLinear) return null

  const handleClick = () => {
    if (trackingRef.current?.clickTracking) fireTrackingUrls(trackingRef.current.clickTracking)
    trackZoneClick({ zoneId })
  }

  const nativeW = nonLinear.width || 300
  const nativeH = nonLinear.height || 250

  return (
    <div
      className={cn(
        // Centred on the paused frame rather than tucked into the bottom-right
        // corner. The corner placement kept it clear of the paused image, but
        // it read as an unrelated floating widget rather than part of the
        // pause state — and it collided with the player controls that also
        // live in that corner.
        "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 overflow-hidden rounded-lg shadow-2xl border border-white/10 bg-black/90 pointer-events-auto",
        className,
      )}
      style={{ width: nativeW, height: nativeH }}
    >
      <Badge
        variant="secondary"
        className="absolute top-1.5 left-1.5 z-10 text-[9px] px-1.5 py-0 h-4 bg-black/70 text-white/70 border-0"
      >
        {t("ad")}
      </Badge>
      {/* bottom-right, not top-right: same ExoClick-creative-close-button
          collision documented on StickyBannerAd.tsx — this placement was
          never moved when the other four sitewide ad widgets were. */}
      <div className="absolute bottom-1.5 right-1.5 z-10 flex items-center gap-1">
        <span className="text-[10px] text-white/60 bg-black/60 rounded-full px-1.5 py-0.5 tabular-nums select-none">
          {t("remainingS", { remaining })}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onDismiss() }}
          className="h-5 w-5 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground ring-2 ring-white/90 shadow-md flex items-center justify-center transition-colors"
          aria-label={t("closeAd")}
        >
          <X className="h-3 w-3" />
        </button>
      </div>

      {nonLinear.clickThrough ? (
        <a href={nonLinear.clickThrough} target="_blank" rel="noopener noreferrer" onClick={handleClick} className="block w-full h-full">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={nonLinear.imageUrl} alt={t("advertisement")} className="w-full h-full object-cover" />
        </a>
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={nonLinear.imageUrl} alt={t("advertisement")} className="w-full h-full object-cover" />
      )}
    </div>
  )
}
